import React from "react";
import { Typography, Box, Skeleton } from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { ProvidenceData } from "../../../services/api/dashboard/dashboardApi";

interface ProvidenceChartProps {
  data: ProvidenceData[] | undefined;
  loading: boolean;
}

export default function ProvidenceChart({
  data,
  loading,
}: ProvidenceChartProps) {
  // Ordenar por quantidade
  const sortedData = React.useMemo(() => {
    if (!data) return [];
    return [...data].sort((a, b) => b.count - a.count);
  }, [data]);

  // Função para abreviar nomes longos
  const formatProvidenceName = (name: string) => {
    if (name.length > 15) {
      return name.substring(0, 12) + "...";
    }
    return name;
  };

  // Custom tooltip
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <Box
          sx={{
            backgroundColor: "white",
            border: "1px solid #ccc",
            borderRadius: 1,
            p: 1.5,
            boxShadow: 2,
            maxWidth: 250,
          }}
        >
          <Typography variant="body2" fontWeight="bold">
            {data.providenceName}
          </Typography>
          <Typography variant="body2" color="primary">
            Atividades: {data.count}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Tempo execução: {data.avgExecutionTime?.toFixed(1) || "N/A"}h
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Tempo total: {data.avgTotalTime?.toFixed(1) || "N/A"}h
          </Typography>
        </Box>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <Box>
        <Skeleton variant="text" width="40%" height={32} />
        <Skeleton variant="rectangular" height={300} sx={{ mt: 2 }} />
      </Box>
    );
  }

  return (
    <Box>
      <Typography 
        variant="h6" 
        component="h3" 
        sx={{ 
          fontSize: "1rem", 
          fontWeight: 600, 
          mb: 2,
          color: "text.primary"
        }}
      >
        Atividades por Providência
      </Typography>
      
      <Box sx={{ width: "100%", height: 400 }}>
        <ResponsiveContainer>
          <BarChart
            data={sortedData}
            layout="vertical"
            margin={{ top: 5, right: 30, left: 120, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis type="number" style={{ fontSize: 12 }} />
            <YAxis 
              type="category" 
              dataKey="providenceName" 
              width={110}
              style={{ fontSize: 11 }}
              tick={{ fontSize: 11 }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            <Bar
              dataKey="count"
              fill="#2e7d32"
              name="Quantidade"
              radius={[0, 4, 4, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
}

